import { defineStyleConfig, StyleFunctionProps } from '@chakra-ui/react';

const Button = defineStyleConfig({
  // ...chakraTheme.components.Button,
  // 1. We can update the base styles
  baseStyle: () => ({
    borderRadius: 30,
  }),
  // 2. We can add a new button size or extend existing
  sizes: {
    lg: (props: StyleFunctionProps) => ({
      minH: '48px',
      minW: '48px',
      h: '48px',
      paddingLeft: props.theme.space[8],
      paddingRight: props.theme.space[8],
    }),
    md: (props: StyleFunctionProps) => ({
      minH: '36px',
      minW: '36px',
      h: '36px',
      paddingLeft: props.theme.space[6],
      paddingRight: props.theme.space[6],
    }),
    sm: (props: StyleFunctionProps) => ({
      minH: '32px',
      minW: '32px',
      h: '32px',
      paddingLeft: props.theme.space[4],
      paddingRight: props.theme.space[4],
    }),
    xs: (props: StyleFunctionProps) => ({
      minH: '24px',
      minW: '24px',
      h: '24px',
      paddingLeft: props.theme.space[3],
      paddingRight: props.theme.space[3],
    }),
  },
  // 3. We can add a new visual variant
  variants: {
    solid: () => ({
      bg: 'palette.primary.50',
      color: 'palette.text.primary.100',
      borderRadius: 'full',
      _hover: {
        bg: 'palette.surface.primary.30',
        color: 'palette.text.primary.100',
        _disabled: {
          bg: 'palette.surface.secondary.95',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'palette.surface.secondary.95',
        color: 'palette.text.secondary.90',
      },
    }),
    solidTone: () => ({
      bg: 'palette.surface.primary.90',
      color: 'palette.text.secondary.50',
      borderRadius: 'full',
      _hover: {
        bg: 'palette.surface.primary.60',
        color: 'palette.text.primary.100',
        _disabled: {
          bg: 'palette.surface.secondary.95',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'palette.surface.secondary.95',
        color: 'palette.text.secondary.90',
      },
    }),
    outline: (props: StyleFunctionProps) => ({
      bg: 'transparent',
      color: 'palette.text.primary.50',
      borderRadius: 'full',
      borderWidth: 0,
      boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.stroke.primary[50]}`,
      _hover: {
        bg: props.theme.colors.palette.primary[90],
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.stroke.primary[50]}`,
        _disabled: {
          boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.stroke.secondary[95]}`,
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.stroke.secondary[95]}`,
        // bg: "palette.primary.80",
        color: 'palette.text.secondary.90',
      },
    }),
    solidIcon: () => ({
      bg: 'palette.primary.50',
      color: 'palette.text.primary.100',
      borderRadius: 'full',
      padding: '0 !',
      _hover: {
        bg: 'palette.primary.40',
        color: 'palette.text.primary.100',
        _disabled: {
          bg: 'palette.surface.secondary.95',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'palette.surface.secondary.95',
        color: 'palette.text.secondary.90',
      },
    }),
    outlineIcon: (props: StyleFunctionProps) => ({
      bg: 'transparent',
      color: 'palette.text.primary.50',
      borderRadius: 'full',
      padding: '0 !',
      boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.primary[50]}`,
      _hover: {
        bg: props.theme.colors.palette.primary[90],
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.primary[50]}`,
        _disabled: {
          boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.secondary[95]}`,
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.secondary[95]}`,
        color: 'palette.text.secondary.90',
      },
    }),
    ghost: () => ({
      bg: 'transparent',
      color: 'palette.text.primary.50',
      borderRadius: 'full',
      _hover: {
        bg: 'palette.primary.90',
        color: 'palette.text.primary.30',
        _disabled: {
          bg: 'transparent',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'transparent',
        color: 'palette.text.secondary.90',
      },
    }),
    ghostDanger: () => ({
      bg: 'transparent',
      color: 'palette.errors.50',
      borderRadius: 'full',
      _hover: {
        color: 'palette.errors.50',
        bg: 'palette.errors.80',
        _disabled: {
          bg: 'transparent',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'transparent',
        color: 'palette.text.secondary.90',
      },
    }),
    danger: (props: StyleFunctionProps) => ({
      bg: 'transparent',
      color: 'palette.errors.50',
      boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.errors[50]}`,
      borderRadius: 'full',
      _hover: {
        color: 'palette.primary.100',
        bg: 'palette.errors.50',
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.errors[50]}`,
        _disabled: {
          bg: 'transparent',
          color: 'palette.text.secondary.90',
          boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.secondary[95]}`,
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'transparent',
        color: 'palette.text.secondary.90',
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.secondary[95]}`,
      },
    }),
    link: () => ({
      bg: 'transparent',
      color: 'palette.primary.50',
      borderRadius: 'full',
      textDecoration: 'underline',
      padding: '0 !',
      minH: 'auto !',
      h: 'auto !',
      _hover: {
        bg: 'transparent',
        color: 'palette.primary.30',
        _disabled: {
          bg: 'transparent',
          color: 'palette.text.secondary.90',
        },
      },
      _disabled: {
        opacity: 1,
        bg: 'transparent',
        color: 'palette.text.secondary.90',
      },
    }),
    group: (props: StyleFunctionProps) => ({
      bg: 'transparent',
      color: 'palette.primary.0',
      borderRadius: 4,
      boxShadow: `inset 0px 0px 0px 1px ${props.theme.colors.palette.info[80]}`,
      _hover: {
        bg: props.theme.colors.palette.pending['0.5'],
        boxShadow: `inset 0px 0px 0px 2px ${props.theme.colors.palette.info[80]}`,
      },
    }),
    dropdown: (props: StyleFunctionProps) => ({
      bg: 'transparent',
      borderRadius: props.theme.space[6],
      _hover: {
        bg: 'palette.neutral.90',
      },
      _expanded: {
        bg: 'palette.neutral.90',
      },
    }),
    filter: () => ({
      bg: 'transparent',
      borderRadius: 4,
      borderWidth: 1,
      pr: 3,
      borderColor: 'palette.neutral.60',
      fontSize: 'sm',
      _hover: {
        bg: 'palette.neutral.90',
        borderColor: 'palette.pending.10',
      },
      _expanded: {
        bg: 'palette.neutral.90',
        borderColor: 'palette.pending.10',
      },
    }),
    profile: (props: StyleFunctionProps) => ({
      bg: 'none',
      border: '1px solid',
      borderColor: 'palette.primary.80',
      borderRadius: props.theme.space[6],
      p: '0 3px',
      _hover: {
        bg: 'none',
        borderColor: 'palette.primary.50',
      },
      _expanded: {
        bg: 'none',
        borderColor: 'palette.primary.50',
      },
    }),
    transparent: () => ({
      bg: 'none',
      minWidth: 'auto',
      _hover: {
        bg: 'transparent',
      },
      _focusVisible: {
        boxShadow: 'none',
      },
    }),
    pagination: () => ({
      bg: 'none',
      minW: 'auto',
      p: '0 !',
      _hover: {
        bg: 'transparent',
      },
      _disabled: {
        opacity: 1,
      },
    }),
    complex: () => ({
      h: 37.5,
      w: 41.5,
      m: 0,
      p: '0 !',
      background: 'palette.neutral.90',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
    }),
  },
  // // 6. We can overwrite defaultProps
  defaultProps: {
    size: 'md', // default is md
    variant: 'solid', // default is solid
    // colorScheme: 'gray', // default is gray
  },
});

export default Button;
